// @ts-nocheck
import React, { useState, useEffect, useMemo } from "react";
import { Skeleton } from "../Skeleton";
import { useDispatch, useSelector } from "react-redux";
import { generateClient } from "aws-amplify/api";
import { fetchDatasetsForCurrentUser } from "../../store/datasetSlice";
import { isCuratedDataset } from "../../utils/datasetQueryApi";
import { DEFAULT_RANGE_LOOKBACK_DAYS, datasetColumnNames } from "../../utils/seriesSources";
import { buildDatasetSelectOptions } from "../../utils/datasetSelectOptions";
import type { DataSourceFormState } from "../../types/composedForecast";
import type { AppDispatch, RootState } from "../../store";

import Select from "../ReusableComponents/Select";
const client = generateClient();

interface DatasetRecord {
  id: string;
  timeValue: string;
  s3Path: string;
  fileName: string;
  recordCount?: number;
  fileSize?: number;
}

interface DatasetSourceConfigProps {
  dataSource: DataSourceFormState;
  onUpdate: (updated: DataSourceFormState) => void;
  /** The view has a forecast: its History before the forecast decides the window, so this source's own lookback is hidden. */
  viewHasForecast?: boolean;
}

const DatasetSourceConfig: React.FC<DatasetSourceConfigProps> = ({
  dataSource,
  onUpdate,
  viewHasForecast = false,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { personalDatasets, organizationDatasets, loading } = useSelector(
    (state: RootState) => state.datasets
  );
  const [records, setRecords] = useState<DatasetRecord[]>([]);
  const [recordsLoading, setRecordsLoading] = useState(false);

  useEffect(() => {
    if (loading === "idle") dispatch(fetchDatasetsForCurrentUser());
  }, [dispatch, loading]);

  // Fetch records when dataset changes; a curated dataset is read by column over a range, so it lists none
  useEffect(() => {
    if (dataSource.datasetId && !dataSource.curated) {
      fetchRecords(dataSource.datasetId);
    } else {
      setRecords([]);
    }
  }, [dataSource.datasetId, dataSource.curated]);

  const fetchRecords = async (datasetId: string) => {
    setRecordsLoading(true);
    try {
      const { data } = await client.graphql({
        query: /* GraphQL */ `
          query ListDatasetRecords($datasetId: ID!, $sortDirection: ModelSortDirection, $limit: Int) {
            dataSetRecordsByDatasetIdAndTimeValue(
              datasetId: $datasetId
              sortDirection: $sortDirection
              limit: $limit
            ) {
              items {
                id
                timeValue
                s3Path
                fileName
                recordCount
                fileSize
              }
            }
          }
        `,
        variables: { datasetId, sortDirection: "DESC", limit: 50 },
      });
      setRecords(data.dataSetRecordsByDatasetIdAndTimeValue?.items || []);
    } catch (err) {
      console.error("Error fetching dataset records:", err);
      setRecords([]);
    } finally {
      setRecordsLoading(false);
    }
  };

  // A saved record missing from the fetched list (deleted, or past the 50 newest) still shows as the pick instead of reading as "no records"
  const recordOptions =
    dataSource.datasetRecordId && !records.some((r) => r.id === dataSource.datasetRecordId)
      ? [{ id: dataSource.datasetRecordId, fileName: dataSource.datasetRecordFileName, s3Path: dataSource.datasetRecordS3Path, saved: true }, ...records]
      : records;

  const allDatasets = [
    ...Object.values(personalDatasets),
    ...Object.values(organizationDatasets),
  ];

  const datasetOptions = useMemo(
    () => buildDatasetSelectOptions(Object.values(personalDatasets), Object.values(organizationDatasets)),
    [personalDatasets, organizationDatasets]
  );

  const pickedDataset = allDatasets.find((d) => d.id === dataSource.datasetId) || null;
  const curatedColumns = pickedDataset && dataSource.curated ? datasetColumnNames(pickedDataset.columns) : [];

  const handleDatasetSelect = (datasetId: string) => {
    const dataset = allDatasets.find((d) => d.id === datasetId);
    if (dataset) {
      // A curated (Parquet) dataset is one series across every upload: pick a column, not a file (stage 5, decision 4)
      const curated = isCuratedDataset(dataset);
      const [firstColumn] = curated ? datasetColumnNames(dataset.columns) : [];
      onUpdate({
        ...dataSource,
        datasetId: dataset.id,
        datasetName: dataset.name,
        datasetRecordId: undefined,
        datasetRecordS3Path: undefined,
        datasetRecordFileName: undefined,
        curated: curated || undefined,
        column: curated ? firstColumn : undefined,
        operation: curated ? null : dataSource.operation,
        sourceName: dataSource.sourceName || (curated && firstColumn ? `${dataset.name} · ${firstColumn}` : dataset.name),
      });
    }
  };

  const handleRecordSelect = (recordId: string) => {
    const record = recordOptions.find((r) => r.id === recordId);
    if (record) {
      onUpdate({
        ...dataSource,
        datasetRecordId: record.id,
        datasetRecordS3Path: record.s3Path,
        datasetRecordFileName: record.fileName,
      });
    }
  };

  const formatDate = (isoString: string) => {
    try {
      return new Date(isoString).toLocaleString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return isoString;
    }
  };

  if (loading === "pending") {
    return (
      // Field-shaped placeholder: the select lands in exactly this box once the options load
      <Skeleton className="h-eai-control-lg w-full" radius="md" />
    );
  }

  return (
    <div className="space-y-3">
      {/* Dataset selector */}
      <div>
        <label className="eai-meta-label mb-2 block">
          Dataset
        </label>
        <Select
          searchable
          aria-label="Dataset"
          placeholder="Select a dataset..."
          value={dataSource.datasetId || ""}
          onChange={(e) => handleDatasetSelect(e.target.value)}
          options={datasetOptions}
          containerClassName="w-full"
        />
      </div>

      {/* Curated dataset: a column over a range, and how far back to read when the view has no forecast */}
      {dataSource.datasetId && dataSource.curated && (
        <>
          <div>
            <label className="eai-meta-label mb-2 block">Column</label>
            {curatedColumns.length === 0 ? (
              <p className="py-1 font-inter text-eai-body-xs text-eai-ink-faint">This dataset has no columns recorded yet.</p>
            ) : (
              <Select
                searchable={curatedColumns.length > 8}
                value={dataSource.column || ""}
                onChange={(e) => onUpdate({ ...dataSource, column: e.target.value })}
                containerClassName="w-full"
              >
                {curatedColumns.map((column) => (
                  <option key={column} value={column}>
                    {column}
                  </option>
                ))}
              </Select>
            )}
            <p className="mt-1 font-inter text-eai-body-xs text-eai-ink-faint">
              Curated dataset: read as one series across every upload. One line per row; add another row for another column.
            </p>
          </div>
          <div>
            <label className="eai-meta-label mb-2 block">Lookback Days</label>
            {viewHasForecast ? (
              <p className="font-inter text-eai-body-xs text-eai-ink-faint">
                This view has a forecast, so <span className="text-eai-ink-muted">History before the forecast</span> at the
                top sets how much of this source is shown.
              </p>
            ) : (
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <input
                type="number"
                value={dataSource.lookbackDays || DEFAULT_RANGE_LOOKBACK_DAYS}
                onChange={(e) => onUpdate({ ...dataSource, lookbackDays: parseInt(e.target.value) || DEFAULT_RANGE_LOOKBACK_DAYS })}
                min="1"
                max="730"
                className="eai-input w-24 shrink-0 sm:w-32"
              />
              <span className="font-inter text-eai-body-xs text-eai-ink-faint">days of history shown, ending at its newest data (the view has no forecast)</span>
            </div>
            )}
          </div>
        </>
      )}

      {/* Record selector — shown after a non-curated dataset is selected */}
      {dataSource.datasetId && !dataSource.curated && (
        <div>
          <label className="eai-meta-label mb-2 block">
            Dataset Record
          </label>
          {recordsLoading ? (
            <Skeleton className="h-eai-control-lg w-full" radius="md" />
          ) : recordOptions.length === 0 ? (
            <p className="py-1 font-inter text-eai-body-xs text-eai-ink-faint">No records found for this dataset.</p>
          ) : (
            <Select
              value={dataSource.datasetRecordId || ""}
              onChange={(e) => handleRecordSelect(e.target.value)}
              containerClassName="w-full"
            >
              <option value="">Select a record...</option>
              {recordOptions.map((record) => (
                <option key={record.id} value={record.id}>
                  {record.saved
                    ? `${record.fileName || "Saved record"} (no longer listed)`
                    : `${formatDate(record.timeValue)} — ${record.fileName}${record.recordCount ? ` (${record.recordCount} rows)` : ""}`}
                </option>
              ))}
            </Select>
          )}
        </div>
      )}

      {allDatasets.length === 0 && loading !== "pending" && (
        <p className="font-inter text-eai-body-xs text-eai-ink-faint">No datasets found.</p>
      )}
    </div>
  );
};

export default DatasetSourceConfig;
